<template>
  <div
    class="avatar"
    :class="[
      props.size ? `avatar--${props.size}` : '',
      props.type === 'square' ? 'is-square' : '',
      props.image ? '' : 'bg-gray-100',
    ]"
  >
    <img
      v-if="props.image"
      :src="src"
      :alt="props.name"
      loading="lazy"
    >
    <span v-else-if="initials">
      {{ initials }}
    </span>
    <Icon
      v-else
      class="icon text-gray-500"
      :name="props.icon || 'user-03'"
    />
  </div>
</template>

<script setup>
const props = defineProps(buildProps({
  type: {
    type: String,
    values: ['circle', 'square'],
    default: 'circle',
  },
  size: {
    type: String,
    values: ['xxs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl'],
    default: 'md',
  },
  image: {
    type: String,
    default: null,
  },
  name: {
    type: String,
    default: null,
  },
  icon: {
    type: String,
    default: null,
  },
}));

// Computed
const initials = computed(() => {
  if (!(props.name && props.name.trim().length)) {
    return false;
  }

  return props.name[0];
});
const src = computed(() => {
  if (useRuntimeConfig().public.imagekit) {
    return `${useRuntimeConfig().public.imagekit}/tr:w-200,h-200,fo-auto/${props.image}`;
  }

  return props.image;
});
</script>

<style scoped>
.avatar {
  @apply inline-flex items-center justify-center flex-shrink-0 z-10 overflow-hidden border border-gray-200;
  @apply text-xs font-medium leading-none text-gray-800 w-8 h-8;
  @apply rounded-full;

  .icon {
    @apply h-auto;
  }

  /* Sizes */
  &--xxs {
    @apply text-[0.5rem] w-4 h-4;

    &.is-square {
      @apply rounded;
    }

    .icon {
      @apply w-2;
    }
  }

  &--xs {
    @apply text-xs w-6 h-6;

    &.is-square {
      @apply rounded;
    }

    .icon {
      @apply w-3.5;
    }
  }

  &--sm {
    @apply text-sm w-8 h-8;

    &.is-square {
      @apply rounded;
    }

    .icon {
      @apply w-4;
    }
  }

  &--md {
    @apply text-base w-10 h-10;

    &.is-square {
      @apply rounded-md;
    }

    .icon {
      @apply w-5;
    }
  }

  &--lg {
    @apply text-lg w-12 h-12;

    &.is-square {
      @apply rounded-md;
    }

    .icon {
      @apply w-5;
    }
  }

  &--xl {
    @apply text-xl w-14 h-14;

    &.is-square {
      @apply rounded-lg;
    }

    .icon {
      @apply w-6;
    }
  }

  &--2xl {
    @apply text-2xl w-16 h-16;

    &.is-square {
      @apply rounded-lg;
    }

    .icon {
      @apply w-6;
    }
  }

  &--3xl {
    @apply text-2xl w-[6.25rem] h-[6.25rem];

    &.is-square {
      @apply rounded-lg;
    }

    .icon {
      @apply w-10;
    }
  }

  &--4xl {
    @apply text-[2rem] w-[8rem] h-[8rem];

    &.is-square {
      @apply rounded-lg;
    }

    .icon {
      @apply w-12;
    }
  }
}
</style>
